/**
 * riskStatsConverters.js
 *
 * DashboardRiskStatsPage から抽出された純粋関数群
 * リスク統計画面固有のデータ集計・判定ロジックを提供
 */

/**
 * アイテムが期日超過かどうか判定
 * @param {Object} item - アイテム
 * @returns {boolean} 期日超過の場合 true
 */
export function isItemOverdue(item) {
  return item.deadlineCheck === '遅延';
}

/**
 * アイテムが完了済みかどうか判定
 * @param {Object} item - アイテム
 * @returns {boolean} 完了している場合 true
 */
export function isRowCompleted(item) {
  const status = item.status || '';
  return String(status) === '完了' || String(status).toLowerCase() === 'done';
}

/**
 * アイテムのタイトルを取得
 * @param {Object} item - アイテム
 * @param {string} type - アイテムタイプ（'problems' または 'risks'）
 * @returns {string} タイトル
 */
export function getItemTitle(item, type) {
  if (type === 'problems') return item.summary || item.title || '(タイトルなし)';
  return item.title || item.summary || '(タイトルなし)';
}

/**
 * アイテム配列を集計し、統計データを生成
 * @param {Array<Object>} items - アイテム配列
 * @param {string} areaField - エリアフィールド名（例: 'actor'）
 * @param {Function} isOverdue - 期日超過判定関数
 * @returns {Object} 集計統計オブジェクト
 */
export function aggregateStats(items, areaField, isOverdue) {
  let total = 0;
  let overdue = 0;
  const byAreaMap = new Map();
  const byOemRegionMap = new Map();

  items.forEach((item) => {
    total += 1;
    const itemOverdue = isOverdue(item);
    if (itemOverdue) overdue += 1;

    const area = item[areaField] || '(未設定)';
    if (!byAreaMap.has(area)) byAreaMap.set(area, { total: 0, overdue: 0, items: [], overdueItems: [], normalItems: [] });
    const areaEntry = byAreaMap.get(area);
    areaEntry.total += 1;
    areaEntry.items.push(item);
    if (itemOverdue) {
      areaEntry.overdue += 1;
      areaEntry.overdueItems.push(item);
    } else {
      areaEntry.normalItems.push(item);
    }

    const oemRegion = item.oemRegion || '(未設定)';
    if (!byOemRegionMap.has(oemRegion)) byOemRegionMap.set(oemRegion, { total: 0, overdue: 0, items: [], overdueItems: [], normalItems: [] });
    const orEntry = byOemRegionMap.get(oemRegion);
    orEntry.total += 1;
    orEntry.items.push(item);
    if (itemOverdue) {
      orEntry.overdue += 1;
      orEntry.overdueItems.push(item);
    } else {
      orEntry.normalItems.push(item);
    }
  });

  const overdueRate = total > 0 ? (overdue / total) * 100 : 0;

  const toArray = (map) =>
    Array.from(map.entries())
      .map(([name, data]) => ({
        name,
        total: data.total,
        overdue: data.overdue,
        normal: data.total - data.overdue,
        items: data.items,
        overdueItems: data.overdueItems,
        normalItems: data.normalItems,
      }))
      .sort((a, b) => b.total - a.total);

  return {
    total,
    overdue,
    overdueRate,
    allItems: items,
    overdueItems: items.filter((i) => isOverdue(i)),
    normalItems: items.filter((i) => !isOverdue(i)),
    byArea: toArray(byAreaMap),
    byOemRegion: toArray(byOemRegionMap),
  };
}
